import React from "react";
import { ShieldCheck } from "lucide-react";

const HERO_IMAGE = "/assets/images/logo-landing.jpg";
const PATTERN = "/assets/images/blob.svg";

const SectionBlock = ({ number, title, children }) => (
  <section className="border-b border-slate-100 pb-8 last:border-b-0 last:pb-0">
    <div className="flex gap-4">
      <div className="w-11 h-11 rounded-2xl bg-[#ff6600]/10 text-[#ff6600] flex items-center justify-center font-black shrink-0">
        {number}
      </div>

      <div>
        <h3 className="text-2xl font-black text-[#06183f] mb-3">{title}</h3>
        <div className="space-y-4 text-slate-700 leading-8">{children}</div>
      </div>
    </div>
  </section>
);

const PrivacyPage = () => {
  return (
    <main className="bg-slate-50 overflow-hidden">
      <section
        className="relative pt-36 pb-24 bg-[#06183f] text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(6, 24, 63, 0.88), rgba(6, 24, 63, 0.94)), url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <img
          src={PATTERN}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
        />

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="w-10 h-[3px] bg-[#ff6600]" />
            <span className="text-[#ff6600] font-black tracking-[0.2em] text-sm uppercase">
              AKSUBEB Privacy Statement
            </span>
            <span className="w-10 h-[3px] bg-[#ff6600]" />
          </div>

          <h1
            className="text-5xl md:text-7xl font-black leading-tight"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Privacy Statement
          </h1>

          <p className="text-white/80 mt-5 max-w-3xl mx-auto leading-relaxed">
            This page explains how AKSUBEB collects, uses, protects and manages
            personal information submitted through its official website.
          </p>

          <p className="mt-4 text-[#ff6600] font-bold">Effective Date: May 2026</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="bg-white rounded-[34px] p-6 md:p-10 shadow-xl border border-slate-100">
          <div className="flex flex-col md:flex-row md:items-center gap-5 mb-10 bg-slate-50 rounded-[26px] p-6 border border-slate-100">
            <div className="w-16 h-16 rounded-2xl bg-[#ff6600]/10 text-[#ff6600] flex items-center justify-center">
              <ShieldCheck size={30} />
            </div>

            <div>
              <h2 className="text-3xl font-black text-[#06183f]">
                Akwa Ibom State Universal Basic Education Board
              </h2>
              <p className="text-slate-500 mt-1">Website: subeb.ak.gov.ng</p>
            </div>
          </div>

          <div className="space-y-9">
            <div className="text-slate-700 leading-8 space-y-4">
              <p>
                The Akwa Ibom State Universal Basic Education Board, AKSUBEB,
                respects your privacy and is committed to protecting the
                personal information of visitors, pupils, parents, teachers,
                school administrators, contractors, partners, and members of the
                public who interact with this website.
              </p>

              <p>
                This Privacy Statement explains how AKSUBEB may collect, use,
                store, protect, and share personal information through
                subeb.ak.gov.ng and related digital services.
              </p>
            </div>

            <SectionBlock number="01" title="Information We May Collect">
              <p>When you visit or use this website, AKSUBEB may collect:</p>

              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Personal identification information such as name, phone
                  number, email address, residential address, school,
                  designation, staff number, or details submitted through forms.
                </li>
                <li>
                  Educational and administrative information such as school
                  data, teacher records, project records, pupil enrolment data,
                  monitoring reports, and education planning records.
                </li>
                <li>
                  Technical information such as IP address, browser type, device
                  information, pages visited, time spent on the website, and
                  usage data.
                </li>
                <li>
                  Communication information such as messages, complaints,
                  enquiries, feedback, reports, or documents submitted through
                  the website.
                </li>
              </ul>
            </SectionBlock>

            <SectionBlock number="02" title="How We Use Your Information">
              <p>AKSUBEB may use collected information for the following purposes:</p>

              <ul className="list-disc pl-6 space-y-2">
                <li>
                  To provide official information about basic education
                  programmes, projects, policies, and public services.
                </li>
                <li>
                  To process enquiries, complaints, applications, requests, and
                  feedback.
                </li>
                <li>
                  To support school data management, teacher data management,
                  project monitoring, reporting, planning, and public
                  accountability.
                </li>
                <li>
                  To improve website performance, user experience, security, and
                  accessibility.
                </li>
                <li>To communicate with users where necessary.</li>
                <li>
                  To comply with legal, regulatory, administrative, and
                  public-sector obligations.
                </li>
              </ul>
            </SectionBlock>

            <SectionBlock number="03" title="Lawful Basis for Processing Personal Information">
              <p>
                AKSUBEB processes personal information only where there is a
                valid reason to do so. This may include public interest, legal
                obligation, consent, performance of official duties, service
                delivery, record keeping, and legitimate administrative purposes.
              </p>

              <p>
                Where consent is required, users will be informed before their
                personal information is collected or used.
              </p>
            </SectionBlock>

            <SectionBlock number="04" title="Protection of Children’s Information">
              <p>
                AKSUBEB recognises the need to protect information relating to
                children and pupils.
              </p>

              <p>
                Personal information about pupils will be collected and processed
                only for lawful educational, administrative, planning,
                monitoring, and public service purposes.
              </p>

              <p>
                Such information will be handled with appropriate care and will
                not be published publicly unless authorised by law, official
                policy, or valid consent.
              </p>
            </SectionBlock>

            <SectionBlock number="05" title="Cookies and Website Analytics">
              <p>
                This website may use cookies or similar technologies to improve
                user experience, monitor website performance, analyse traffic,
                and strengthen security.
              </p>

              <p>
                Users may disable cookies through their browser settings.
                However, some parts of the website may not function properly if
                cookies are disabled.
              </p>
            </SectionBlock>

            <SectionBlock number="06" title="Sharing of Information">
              <p>
                AKSUBEB will not sell, rent, or trade personal information.
              </p>

              <p>
                Personal information may be shared only where necessary with
                authorised government agencies, education partners, service
                providers, regulatory bodies, or other approved parties for
                lawful public-sector purposes.
              </p>

              <p>
                Where third-party service providers process data on behalf of
                AKSUBEB, they are expected to protect such data and use it only
                for the authorised purpose.
              </p>
            </SectionBlock>

            <SectionBlock number="07" title="Data Security">
              <p>
                AKSUBEB will take reasonable administrative, technical, and
                organisational measures to protect personal information against
                unauthorised access, loss, misuse, alteration, disclosure, or
                destruction.
              </p>

              <p>
                Although reasonable safeguards will be applied, no website or
                digital system can guarantee absolute security. Users are advised
                to avoid submitting sensitive information through unsecured
                channels.
              </p>
            </SectionBlock>

            <SectionBlock number="08" title="Data Retention">
              <p>
                AKSUBEB will retain personal information only for as long as
                necessary to fulfil the purpose for which it was collected,
                comply with legal and administrative requirements, resolve
                disputes, maintain public records, or support education planning
                and reporting.
              </p>
            </SectionBlock>

            <SectionBlock number="09" title="Your Privacy Rights">
              <p>Subject to applicable law, users may have the right to:</p>

              <ul className="list-disc pl-6 space-y-2">
                <li>Request access to their personal information.</li>
                <li>Request correction of inaccurate or incomplete information.</li>
                <li>Withdraw consent where processing is based on consent.</li>
                <li>
                  Request deletion of personal information where legally
                  permitted.
                </li>
                <li>Object to unlawful or unnecessary processing.</li>
                <li>Request information on how their data is being used.</li>
              </ul>
            </SectionBlock>

            <SectionBlock number="10" title="Links to Other Websites">
              <p>
                This website may contain links to other government, partner, or
                third-party websites. AKSUBEB is not responsible for the privacy
                practices, security, or content of external websites.
              </p>

              <p>
                Users should review the privacy statements of any external
                websites they visit.
              </p>
            </SectionBlock>

            <SectionBlock number="11" title="Updates to This Privacy Statement">
              <p>
                AKSUBEB may update this Privacy Statement from time to time to
                reflect legal, operational, technological, or policy changes.
              </p>

              <p>
                Any updated version will be published on this website with a
                revised effective date.
              </p>
            </SectionBlock>

            <SectionBlock number="12" title="Contact">
              <p>
                For questions, requests, complaints, or concerns about this
                Privacy Statement or the handling of personal information on this
                website, please contact:
              </p>

              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                <p className="font-bold text-[#06183f]">
                  Akwa Ibom State Universal Basic Education Board
                </p>
                <p>Uyo, Akwa Ibom State, Nigeria</p>
                <p>Website: subeb.ak.gov.ng</p>
                <p>Email: info@subeb.ak.gov.ng</p>
              </div>
            </SectionBlock>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPage;